"use client";

import { useMemo } from "react";
import { usePathname, useRouter } from "next/navigation";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PlanBadge } from "@/components/ui/plan-badge";
import { Icon } from "@/components/ui/icon";
import type { JobTabValue } from "@/features/jobs/components/view/layout/job-tab-items";
import { useVisibleJobTabItems } from "@/features/jobs/components/view/layout/use-visible-job-tab-items";

type Tab = JobTabValue;

interface JobNavigationTabsProps {
  jobId: string;
  loading?: boolean;
}

/** Resolves which job tab a pathname belongs to (defaults to pipeline).
 *  Distinct from `resolveActiveTab` in `components/search/search-tabs.ts`,
 *  which maps talent-search routes — same name, different route domain and
 *  signature by design (ARBI-851). */
export function resolveActiveTab(pathname: string, jobId: string): Tab {
  const base = `/jobs/${jobId}`;
  if (pathname.startsWith(`${base}/arbitrator`)) return "arbitrator";
  if (pathname.startsWith(`${base}/sequences`)) return "sequences";
  if (pathname.startsWith(`${base}/tasks`)) return "tasks";
  if (pathname.startsWith(`${base}/conversations`)) return "conversations";
  if (pathname.startsWith(`${base}/scorecards`)) return "scorecards";
  if (pathname.startsWith(`${base}/agent`)) return "agent";
  return "pipeline";
}

export function JobNavigationTabs({ jobId, loading }: JobNavigationTabsProps) {
  const pathname = usePathname();
  const router = useRouter();
  const tabItems = useVisibleJobTabItems();

  const activeTab = useMemo(
    () => resolveActiveTab(pathname ?? "", jobId),
    [pathname, jobId],
  );

  if (loading) {
    return (
      <div className="flex items-center overflow-x-auto border-b border-border px-4 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="inline-flex h-8 items-center gap-0.5 rounded-md bg-contrast/3 p-0.5">
          {tabItems.map((tab) => (
            <div
              key={tab.value}
              className="flex h-full items-center gap-1.5 rounded-md px-3"
            >
              <div className="size-4 rounded bg-contrast/6 shimmer shimmer-bg text-foreground" />
              <div
                className="h-3.5 rounded bg-contrast/6 shimmer shimmer-bg text-foreground"
                style={{ width: tab.label.length * 7 }}
              />
              {tab.comingSoon && (
                <div className="h-4 w-[82px] rounded-md bg-contrast/4 shimmer shimmer-bg text-foreground" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      data-tour="pipe-tabs"
      className="flex items-center overflow-x-auto border-b border-border px-4 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          const next = v as Tab;
          if (next === activeTab) return;
          router.push(`/jobs/${jobId}/${next}`);
        }}
        size="default"
      >
        <TabsList>
          {tabItems.map((tab) =>
            tab.comingSoon ? (
              <div key={tab.value} className="group relative cursor-not-allowed">
                <div className="flex h-full items-center gap-1.5 rounded-md px-3">
                  <Icon
                    name={tab.icon}
                    size={16}
                    className="relative size-4 shrink-0 text-foreground opacity-60 transition-opacity duration-300 group-hover:opacity-0"
                  />
                  <span className="relative whitespace-nowrap text-sm font-medium text-contrast/70 transition-opacity duration-300 group-hover:opacity-0">
                    {tab.label}
                  </span>
                </div>
                <div className="absolute inset-0 rounded-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out">
                  <div className="translate-y-1 group-hover:translate-y-0 transition-transform duration-300 ease-out">
                    <PlanBadge variant="business" />
                  </div>
                </div>
              </div>
            ) : (
              <TabsTrigger key={tab.value} value={tab.value}>
                <Icon name={tab.icon} className="relative text-foreground" />
                {tab.label}
              </TabsTrigger>
            ),
          )}
        </TabsList>
      </Tabs>
    </div>
  );
}
